'use client';

import { TooltipProvider } from '@magicschool/spellbook/components/Tooltip/TooltipProvider';
import { ThemeProvider as NextThemesProvider } from 'next-themes';
import { DirectionProvider } from 'radix-ui/direction';

export type Theme = 'ethereal';
export type Mode = 'light' | 'dark';
export type ThemeName = `${Theme}-${Mode}`;

export const THEME_NAMES: ThemeName[] = ['ethereal-light', 'ethereal-dark'];
export const THEME_STORAGE_KEY = 'ms-theme';

export type ThemeProviderProps = React.ComponentProps<typeof NextThemesProvider> & {
  /** Initial theme applied before the user makes a selection. Defaults to `'ethereal-light'`. Pass `'system'` to respect `prefers-color-scheme`. */
  defaultTheme?: ThemeName | 'system';
  /** Text direction for Radix directional components. Defaults to `'ltr'`. */
  dir?: Parameters<typeof DirectionProvider>['0']['dir'];
};

/**
 * Root provider that supplies theme (light/dark), text direction (LTR/RTL), and tooltip context to all
 * Spellbook components. Wraps `next-themes` `ThemeProvider`, Radix `DirectionProvider`, and `TooltipProvider`.
 * Must be rendered once at the application root. Themes follow the `{theme}-{mode}` naming convention
 * (e.g., `ethereal-light`, `ethereal-dark`) and are persisted to localStorage under the `ms-theme` key.
 *
 * Re-exports `useTheme` (from `next-themes`), `useDirection` (from Radix), and `useIsDarkMode` for
 * consuming theme state in child components.
 *
 * @headless Provides theme, direction, and tooltip context; renders only its children.
 */
export function ThemeProvider({
  children,
  attribute = 'class',
  dir = 'ltr',
  defaultTheme = 'ethereal-light',
  enableSystem = false,
  storageKey = THEME_STORAGE_KEY,
  themes = THEME_NAMES,
  value = {
    dark: 'ethereal-dark',
    'ethereal-dark': 'ethereal-dark',
    light: 'ethereal-light',
    'ethereal-light': 'ethereal-light',
  },
  ...rest
}: ThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute={attribute}
      defaultTheme={defaultTheme}
      enableSystem={enableSystem}
      storageKey={storageKey}
      themes={themes}
      value={value}
      {...rest}
    >
      <DirectionProvider dir={dir}>
        <TooltipProvider>{children}</TooltipProvider>
      </DirectionProvider>
    </NextThemesProvider>
  );
}

export { type UseThemeProps, useTheme } from 'next-themes';
export { useDirection } from 'radix-ui/direction';

export { useIsDarkMode } from './useIsDarkMode';
